/**
 * Create Report Dialog Component
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import { type Dataset, type AnalyseCategory } from '../../../lib/api';
import { NewReportState } from '../useReports';

interface CreateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newReport: NewReportState;
  onReportChange: (report: NewReportState) => void;
  categories: AnalyseCategory[];
  datasets: Dataset[];
  saving: boolean;
  onSubmit: () => void;
}

export function CreateReportDialog({
  open,
  onOpenChange,
  newReport,
  onReportChange,
  categories,
  datasets,
  saving,
  onSubmit,
}: CreateReportDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Report</DialogTitle>
          <DialogDescription>
            Create a new report to visualize and analyze your data.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Report Name *</Label>
            <Input
              placeholder="Enter report name"
              value={newReport.name}
              onChange={(e) => onReportChange({ ...newReport, name: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Enter description (optional)"
              value={newReport.description}
              onChange={(e) => onReportChange({ ...newReport, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={newReport.category.toString()}
                onValueChange={(value) => onReportChange({ ...newReport, category: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No Category</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Dataset</Label>
              <Select
                value={newReport.dataset.toString()}
                onValueChange={(value) => onReportChange({ ...newReport, dataset: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select dataset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No Dataset</SelectItem>
                  {datasets.map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id.toString()}>
                      {dataset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Sharing</Label>
            <Select
              value={newReport.shared_with}
              onValueChange={(value: 'private' | 'company' | 'company_and_customers') =>
                onReportChange({ ...newReport, shared_with: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private - Only me</SelectItem>
                <SelectItem value="company">Company - All company users</SelectItem>
                <SelectItem value="company_and_customers">Shared with Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Lock downloads</Label>
              <p className="text-xs text-muted-foreground">
                Only your company can export. Shared viewers keep read access.
              </p>
            </div>
            <Switch
              checked={newReport.download_lock}
              onCheckedChange={(checked) =>
                onReportChange({ ...newReport, download_lock: checked })
              }
              aria-label="Lock downloads"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!newReport.name.trim() || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
